import { useUserSettingsStore } from '@/stores/user-settings';
import { useUserStore } from '@/stores/user';
import { usePolicyStore } from '@/stores/policy';
import { PolicyAction, UserRoles } from '@/types';
import type { UUID, AccessRole } from '@/types';
import { computed } from 'vue';
import type { Project } from '@/gql/graphql.ts';

import { useDevStore } from '@/stores/dev';

const isDev = import.meta.env.MODE !== 'production';

export function usePermissions() {
    const userSettingsStore = useUserSettingsStore();
    const userStore = useUserStore();
    const policyStore = usePolicyStore();

    function hasAccessRole(accessRole: AccessRole['name']) {
        return userStore.accessRoles.some(
            (role) =>
                role.name === accessRole &&
                role.portfolioId === userSettingsStore.activePortfolio?.id &&
                role.accountId === userSettingsStore.activeOrganisation?.id
        );
    }

    const isJigsEye = computed(() =>
        userStore.accessRoles.some((role) => role.name === UserRoles.JigsEye)
    );

    const isAdmin = computed(() => hasAccessRole(UserRoles.Admin));

    const isProgramManager = computed(() =>
        hasAccessRole(UserRoles.ProgramManager)
    );

    const isProjectManager = computed(() =>
        hasAccessRole(UserRoles.ProjectManager)
    );

    const isUser = computed(() => hasAccessRole(UserRoles.User));

    function hasPermission(
        action: PolicyAction,
        resource: UUID = '*',
        portfolio?: UUID,
        account?: UUID
    ) {
        const result = policyStore.hasPolicyAction(action, {
            account: account ?? userSettingsStore.activeOrganisation?.id,
            portfolio: portfolio ?? userSettingsStore.activePortfolio?.id,
            resource,
        });

        if (isDev) {
            useDevStore().trackPermissionCheck(action, resource, result);
        }

        return result;
    }
    function hasProjectPermission(
        action: PolicyAction,
        project: Project,
        account?: UUID
    ) {
        const projects = [project?.id, project?.parentProjectId].filter(
            Boolean
        );
        const portfolios = [
            project?.masterPortfolioId,
            userSettingsStore.activePortfolio?.id,
        ].filter(Boolean);
        const _account = account ?? userSettingsStore.activeOrganisation?.id;

        // Try all combos of projectId, parentProjectId, masterPortfolioId,  + activePortfolioId until one returns true
        const result = projects.some((resource) => {
            return portfolios.some((portfolio) =>
                policyStore.hasPolicyAction(action, {
                    account: _account,
                    portfolio,
                    resource,
                    resourceType: 'project',
                })
            );
        });

        if (isDev) {
            useDevStore().trackPermissionCheck(
                action,
                project?.id ?? '*',
                result
            );
        }

        return result;
    }

    return {
        hasPermission,
        hasProjectPermission,
        isJigsEye,
        isAdmin,
        isProjectManager,
        isProgramManager,
        isUser,
    };
}
